import {
	BigNum,
	BN,
	VelocityClient,
	MarketType,
	maxSizeForTargetLiabilityWeightBN,
	PerpMarketAccount,
	PRICE_PRECISION_EXP,
	User,
	ZERO,
} from '@velocity-exchange/sdk';
import {
	UIMarket,
	getBaseAssetSymbol,
	getMarginUsedForPosition as getMarginUsedForPosition_dep,
	convertLeverageToMarginRatio,
	getMaxLeverageForMarket,
	getUserMaxLeverageForMarket as getUserMaxLeverageForMarket_dep,
} from '@velocity-exchange/common';
import { PredictionMarketConfigs } from 'src/hooks/predictionMarkets/predictionMarketConfigs';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import NumLib from './NumLib';
import { roundBigNumToDecimalPlace } from '@velocity-exchange/common';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';

export const getSpotLiqPriceNum = (
	user: User,
	marketIndex: number,
	baseSize?: BigNum,
	isLong = true
): number => {
	if (!user || !user.isSubscribed) return 0;

	let baseAssetBN = baseSize ? baseSize.val : undefined;

	if (!isLong && baseAssetBN) {
		baseAssetBN = baseAssetBN.mul(new BN(-1));
	}

	const liqPriceBn = user.spotLiquidationPrice(marketIndex, baseAssetBN);

	const liqPriceNum = BigNum.from(liqPriceBn, PRICE_PRECISION_EXP).toNum();

	return liqPriceNum < 0 ? 0 : liqPriceNum;
};

export const getSpotLiqPriceStr = (
	user: User,
	marketIndex: number,
	baseSize?: BigNum,
	isLong = true
): string => {
	if (!user || !user.isSubscribed) return '';

	const liqPriceNum = getSpotLiqPriceNum(user, marketIndex, baseSize, isLong);

	if (liqPriceNum <= 0) return PRICE_FALLBACK_DISPLAY_VALUE;

	const liqPriceUi = NumLib.formatNum.toDisplayPrice(liqPriceNum);

	return `$${liqPriceUi}`;
};

export const getAccountsInUse = (user: User): number => {
	if (!user || !user.isSubscribed) return 0;
	return (
		user.getActivePerpPositions().length + user.getActiveSpotPositions().length
	);
};

export const baseSizeDisplayForPerpMarket = (
	baseSize: BigNum,
	marketSymbol: string,
	hideValue: boolean,
	decimals?: number
) => {
	const isPredictionmarket =
		PredictionMarketConfigs.isPredictionMarket(marketSymbol);

	if (decimals) {
		baseSize = roundBigNumToDecimalPlace(baseSize, decimals);
	}

	return `${hideValue ? '∗∗∗∗' : baseSize.prettyPrint()} ${
		isPredictionmarket ? `Shares` : getBaseAssetSymbol(marketSymbol)
	}`;
};

export const getMarginUsedForPosition = getMarginUsedForPosition_dep;

export const calculateMaxPositionSize = (
	leverage: number,
	marketAccount: PerpMarketAccount | undefined
): BN => {
	if (!marketAccount) return ZERO;

	const targetMarginRatio = convertLeverageToMarginRatio(leverage);

	return (
		maxSizeForTargetLiabilityWeightBN(
			new BN(targetMarginRatio ?? 0),
			new BN(marketAccount.imfFactor),
			new BN(marketAccount.marginRatioInitial),
			marketAccount
		) ?? ZERO
	);
};

export const getUserMaxLeverageForMarket = (
	marketIndex: number,
	driftClient: VelocityClient,
	userClient: User,
	uiMarket: UIMarket
) => {
	const { maxLeverage } = getMaxLeverageForMarket(
		MarketType.PERP,
		marketIndex,
		driftClient
	);
	const savedMaxLeverage = useMarketSettingsStore
		.getState()
		.getMaxLeverageForMarket(uiMarket.market.symbol);
	const positionMaxLeverage = getUserMaxLeverageForMarket_dep(
		userClient,
		marketIndex,
		{
			regularMaxLeverage: maxLeverage,
			highLeverageMaxLeverage: 0,
			hasHighLeverage: false,
		},
		savedMaxLeverage
	);

	return positionMaxLeverage;
};
